import React, { useMemo } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { ClientOptionsProvider as PSClientOptionsProvider } from '@paintscout/ui'
import { gql } from 'apollo-boost'
import type { OptionsDocument, RatesDocument } from 'paintscout'
import { useAppDocsQuery, AppDocsDocument } from '@paintscout/api'
import { sanitizeOptions } from '@paintscout/util/builder'

interface SaveOptionsResponse {
  options: {
    __typename: 'JSON'
  } & OptionsDocument
  rates: {
    __typename: 'JSON'
  } & RatesDocument
}

export default function ClientOptionsProvider({
  children,
  clientId
}: {
  children: React.ReactNode
  clientId?: string
}) {
  const { data: appDocs } = useAppDocsQuery({ fetchPolicy: 'cache-and-network' })

  const [saveOptions] = useMutation<SaveOptionsResponse>(
    gql`
      mutation saveOptions($options: JSON, $rates: JSON, $saveRates: Boolean!, $saveOptions: Boolean!) {
        options: saveOptions(input: $options) @include(if: $saveOptions)
        rates: saveRates(input: $rates) @include(if: $saveRates)
      }
    `,
    {
      update: (cache, { data }) => {
        cache.writeQuery({
          query: AppDocsDocument,
          data: {
            ...appDocs,
            appDocs: {
              ...appDocs.appDocs,
              options: data.options || appDocs.appDocs.options,
              rates: data.rates || appDocs.appDocs.rates
            }
          }
        })
      }
    }
  )

  const [syncOptions] = useMutation(
    gql`
      mutation syncOptions($optionsRev: String!, $ratesRev: String!) {
        options: syncOptions(rev: $optionsRev)
        rates: syncRates(rev: $ratesRev)
      }
    `,
    {
      update: (cache, { data }) => {
        cache.writeQuery({
          query: AppDocsDocument,
          data: {
            ...appDocs,
            appDocs: {
              ...appDocs.appDocs,
              options: data.options || appDocs.appDocs.options,
              rates: data.rates || appDocs.appDocs.rates
            }
          }
        })
      }
    }
  )

  const sanitizedOptions = useMemo(() => sanitizeOptions(appDocs?.appDocs?.options), [appDocs])

  return (
    <PSClientOptionsProvider
      options={sanitizedOptions}
      rates={appDocs.appDocs.rates}
      clientId={clientId}
      onSync={({ optionsRev, ratesRev }) => {
        return syncOptions({
          variables: {
            optionsRev,
            ratesRev
          },
          optimisticResponse: {
            options: {
              ...appDocs.appDocs.options,
              __typename: 'JSON' as const
            },
            rates: {
              ...appDocs.appDocs.rates,
              __typename: 'JSON' as const
            }
          }
        }).then((res) => ({
          options: res.data.options,
          rates: res.data.rates
        }))
      }}
      onSave={({ options, rates }) => {
        return saveOptions({
          variables: {
            options,
            rates,
            saveOptions: !!options,
            saveRates: !!rates
          },
          optimisticResponse: {
            options: options
              ? {
                  ...options,
                  __typename: 'JSON' as const
                }
              : undefined,
            rates: rates
              ? {
                  ...rates,
                  __typename: 'JSON' as const
                }
              : undefined
          }
        }).then((res) => ({
          options: res.data.options,
          rates: res.data.rates
        }))
      }}
    >
      {children}
    </PSClientOptionsProvider>
  )
}
